import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, MoveRight } from "lucide-react";
import { useLanguage } from "@/i18n";
import { scrollToSection } from "@/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1636138100319-0eb45791963e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxkZXNlcnQlMjBkdW5lJTIwc3Vuc2V0JTIwb3ZlcmxhbmR8ZW58MHx8fHwxNzg4ODk4Mzg4fDA&ixlib=rb-4.1.0&q=85";

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.95, ease: [0.16, 1, 0.3, 1], delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const [coords, setCoords] = useState({ lat: 43.0612, lon: 11.6068 });

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 22 });
  const sy = useSpring(my, { stiffness: 55, damping: 22 });
  const imgX = useTransform(sx, [-0.5, 0.5], [16, -16]);
  const imgY = useTransform(sy, [-0.5, 0.5], [10, -10]);

  useEffect(() => {
    const id = setInterval(() => {
      setCoords((c) => ({
        lat: c.lat + (Math.random() - 0.5) * 0.0012,
        lon: c.lon + (Math.random() - 0.5) * 0.0012,
      }));
    }, 1400);
    return () => clearInterval(id);
  }, []);

  const onMouseMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative min-h-screen overflow-hidden flex flex-col"
      data-testid="hero-section"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src={HERO_IMG}
          alt="Dust Republic 4x4 overland expedition at sunset"
          style={{ x: imgX, y: imgY, scale: 1.08 }}
          className="w-full h-full object-cover warm-grade"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 flex-1 flex flex-col justify-center max-w-[1500px] w-full mx-auto px-5 sm:px-8 pt-28 pb-40"
      >
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-2 h-2 bg-blaze animate-pulse-dot" />
          <span className="font-telemetry text-[11px] sm:text-xs uppercase tracking-[0.25em] text-sand">
            {t.hero.eyebrow}
          </span>
        </motion.div>

        <h1 className="font-display font-black uppercase leading-[0.88] tracking-tight text-[17vw] sm:text-[13vw] lg:text-[9.5rem] text-bone">
          <MaskedLine delay={0.25}>{t.hero.lines[0]}</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-blaze">{t.hero.lines[1]}</span>
          </MaskedLine>
          <MaskedLine delay={0.55}>{t.hero.lines[2]}</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-sm sm:text-base leading-relaxed text-sand/90"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-join-republic-btn"
            onClick={() => scrollToSection("#community")}
            className="group font-telemetry text-xs uppercase tracking-[0.22em] bg-blaze text-ink px-8 py-4 flex items-center gap-3 hover:bg-sand transition-colors duration-300"
          >
            {t.hero.ctaJoin}
            <MoveRight
              size={15}
              className="group-hover:translate-x-1.5 transition-transform duration-300"
            />
          </button>
          <button
            data-testid="hero-cta-explore-calendar-btn"
            onClick={() => scrollToSection("#eventi")}
            className="font-telemetry text-xs uppercase tracking-[0.22em] border border-sand/40 text-sand px-8 py-4 hover:border-blaze hover:text-blaze transition-colors duration-300"
          >
            {t.hero.ctaEvents}
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-0 inset-x-0 z-10"
      >
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 pb-6 flex items-end justify-between gap-4">
          <div className="flex items-center gap-3 border border-sand/15 bg-ink/60 backdrop-blur-sm px-4 py-3">
            <span className="w-1.5 h-1.5 rounded-full bg-blaze animate-pulse-dot" />
            <div className="font-telemetry text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-sand/80">
              <span className="text-blaze">{t.hero.activeBadge}</span>
              <span className="mx-2 text-faint">/</span>
              {t.hero.activeEvent}
            </div>
          </div>
          <div className="hidden sm:block font-telemetry text-[11px] tracking-[0.2em] text-sand/70 uppercase">
            {coords.lat.toFixed(4)}°N — {coords.lon.toFixed(4)}°E
          </div>
          <button
            data-testid="hero-scroll-indicator"
            onClick={() => scrollToSection("#manifesto")}
            className="flex items-center gap-2 font-telemetry text-[10px] uppercase tracking-[0.25em] text-sand/60 hover:text-blaze transition-colors"
          >
            {t.hero.scroll}
            <ArrowDown size={13} className="animate-bounce" />
          </button>
        </div>
      </motion.div>
    </section>
  );
}
